/**
 * Name Prompt Modal
 * Prompts user to enter their name before reserving an item
 */

import { useState } from 'react'
import { Modal } from './Modal'
import { Button } from './Button'

interface NamePromptModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (name: string) => void
  itemTitle: string
  isLoading?: boolean
}

export const NamePromptModal = ({
  isOpen,
  onClose,
  onConfirm,
  itemTitle,
  isLoading = false,
}: NamePromptModalProps) => {
  const [name, setName] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedName = name.trim()
    if (!trimmedName) {
      setError('Please enter your name')
      return
    }

    if (trimmedName.length < 2) {
      setError('Name must be at least 2 characters')
      return
    }

    if (trimmedName.length > 50) {
      setError('Name must be less than 50 characters')
      return
    }

    onConfirm(trimmedName)
  }

  const handleClose = () => {
    if (!isLoading) {
      setName('')
      setError('')
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Reserve Item">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-gray-600 mb-4">
            You're about to reserve: <strong>{itemTitle}</strong>
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Please enter your name so the wishlist owner knows who reserved this item.
            Your name will only be visible to the wishlist owner.
          </p>

          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Your Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setError('')
            }}
            placeholder="Enter your name"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              error ? 'border-red-300' : 'border-gray-300'
            }`}
            disabled={isLoading}
            autoFocus
          />
          {error && (
            <p className="text-red-600 text-sm mt-1">{error}</p>
          )}
        </div>

        <div className="flex gap-3 justify-end pt-4 border-t">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
          >
            Reserve Item
          </Button>
        </div>
      </form>
    </Modal>
  )
}
